import React, { useState, useEffect } from "react";
import { Modal, Button, Spin, message } from "antd";
import { CloseOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { Document, Page, pdfjs } from "react-pdf";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "../../utils/axios";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Set up PDF.js worker using unpkg CDN for version compatibility
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FilePreviewModal = ({
  visible,
  onClose,
  fileUrl,
  fileName,
  fileType,
  watermarkData,
  orgId,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [blobUrl, setBlobUrl] = useState(null);

  const { getIdTokenClaims } = useAuth0();

  useEffect(() => {

    if (visible) {
      setPageNumber(1);
      setScale(1.0);
      setLoading(true);
      setError(null);
      setBlobUrl(null);
    }
  }, [visible, fileUrl]);

  // Fetch file via backend proxy to avoid CORS issues
  useEffect(() => {
    if (!visible || !fileUrl || !orgId) {
      return;
    }

    const fetchFileViaProxy = async () => {
      try {
        console.log('[FilePreviewModal] Fetching PDF via proxy:', { fileUrl, orgId });
        setLoading(true);
        setError(null);

        // Get Auth0 token
        const token = await getIdTokenClaims();
        if (!token) {
          throw new Error("Authentication required");
        }

        // Create FormData with the S3 URL
        const formData = new FormData();
        formData.append("url", fileUrl);

        console.log('[FilePreviewModal] Calling /proxy-file endpoint...');

        // Call backend proxy endpoint
        const response = await axios.post(
          `/proxy-file/${orgId}`,
          formData,
          {
            headers: {
              "API-TOKEN": token.__raw,
            },
            responseType: 'blob', // Important: get response as blob
          }
        );

        console.log('[FilePreviewModal] Response received:', {
          status: response.status,
          headers: response.headers,
          dataSize: response.data.size,
          dataType: response.data.type,
        });

        // Create object URL from blob
        const blob = response.data;
        const objectUrl = URL.createObjectURL(blob);

        console.log('[FilePreviewModal] Blob URL created:', objectUrl);
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error('[FilePreviewModal] Error fetching file:', {
          error: err,
          response: err.response,
          message: err.message,
        });
        setError(`Failed to load file: ${err.response?.data?.error || err.message}`);
        setLoading(false);
      }
    };

    fetchFileViaProxy();

    // Cleanup blob URL when component unmounts or fileUrl changes
    return () => {
      if (blobUrl) {
        console.log('[FilePreviewModal] Revoking blob URL:', blobUrl);
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [visible, fileUrl, orgId]);

  // Prevent right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent keyboard shortcuts
  const handleKeyDown = (e) => {
    // Prevent Ctrl+S, Ctrl+P, Ctrl+C, etc.
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "s" || e.key === "p" || e.key === "c" || e.key === "a")
    ) {
      e.preventDefault();
      return false;
    }
    // Prevent Print Screen
    if (e.key === "PrintScreen") {
      e.preventDefault();
      return false;
    }
  };

  useEffect(() => {
    if (visible) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [visible]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('[FilePreviewModal] Document loaded successfully:', { numPages });
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('[FilePreviewModal] Document load error:', error);
    setError("Failed to load PDF file");
    setLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => {
      const newPage = Math.max(prev - 1, 1);
      return newPage;
    });
  };

  const goToNextPage = () => {
    setPageNumber((prev) => {
      const newPage = Math.min(prev + 1, numPages);
      return newPage;
    });
  };

  const zoomIn = () => {
    setScale((prev) => {
      const newScale = Math.min(prev + 0.2, 2.0);
      return newScale;
    });
  };

  const zoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.2, 0.5);
      return newScale;
    });
  };

  const isPDF = fileType?.toLowerCase() === "pdf";
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
    fileType?.toLowerCase()
  );

  const WatermarkOverlay = () => {
    if (!watermarkData) return null;

    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        {/* Top watermark */}
        <div
          style={{
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "bold",
            textShadow: "1px 1px 2px rgba(255, 255, 255, 0.5)",
          }}
        >
          {watermarkData.userName && <div>{watermarkData.userName}</div>}
        </div>

        {/* Center watermark - repeated pattern */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-45deg)",
            color: "rgba(0, 0, 0, 0.08)",
            fontSize: "48px",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            textShadow: "2px 2px 4px rgba(255, 255, 255, 0.3)",
          }}
        >
          {watermarkData.userName || "CONFIDENTIAL"}
        </div>

        {/* Bottom watermark */}
        <div
          style={{
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.15)",
            fontSize: "12px",
            textShadow: "1px 1px 2px rgba(255, 255, 255, 0.5)",
          }}
        >
          {watermarkData.timestamp && <div>Viewed: {watermarkData.timestamp}</div>}
          {watermarkData.ipAddress && <div>IP: {watermarkData.ipAddress}</div>}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      styles={{ body: { height: "85vh", padding: 0 } }}
      closeIcon={<CloseOutlined />}
    >
      <div
        onContextMenu={handleContextMenu}
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#fafafa",
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>{fileName}</div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", paddingRight: "40px" }}>
            {isPDF && (
              <>
                <Button
                  icon={<ZoomOutOutlined />}
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  size="small"
                />
                <span style={{ fontSize: "12px", minWidth: "60px", textAlign: "center" }}>
                  {Math.round(scale * 100)}%
                </span>
                <Button
                  icon={<ZoomInOutlined />}
                  onClick={zoomIn}
                  disabled={scale >= 2.0}
                  size="small"
                />
                <div style={{ borderLeft: "1px solid #d9d9d9", height: "24px", margin: "0 8px" }} />
                <Button
                  icon={<LeftOutlined />}
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  size="small"
                />
                <span style={{ fontSize: "12px", minWidth: "80px", textAlign: "center" }}>
                  Page {pageNumber} of {numPages || "?"}
                </span>
                <Button
                  icon={<RightOutlined />}
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  size="small"
                />
              </>
            )}
            {isImage && (
              <>
                <Button
                  icon={<ZoomOutOutlined />}
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  size="small"
                />
                <span style={{ fontSize: "12px", minWidth: "60px", textAlign: "center" }}>
                  {Math.round(scale * 100)}%
                </span>
                <Button
                  icon={<ZoomInOutlined />}
                  onClick={zoomIn}
                  disabled={scale >= 2.0}
                  size="small"
                />
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "#f5f5f5",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >

          {!blobUrl && !error && visible && (
            <div style={{ textAlign: "center" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: "#666" }}>Loading file...</div>
            </div>
          )}

          {error && (
            <div style={{ textAlign: "center", color: "red" }}>
              <div>{error}</div>
              <Button onClick={onClose} style={{ marginTop: 16 }}>
                Close
              </Button>
            </div>
          )}

          {blobUrl && !error && (
            <div style={{ position: "relative" }}>
              {isPDF && (
                <>
                  <Document
                    file={blobUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div style={{ textAlign: "center" }}>
                        <Spin size="large" />
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </>
              )}

              {isImage && (
                <>
                  <img
                    src={blobUrl}
                    alt={fileName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "calc(85vh - 100px)",
                      transform: `scale(${scale})`,
                      transition: "transform 0.2s",
                      pointerEvents: "none",
                    }}
                    onError={(e) => {
                      setError("Failed to load image");
                      setLoading(false);
                    }}
                    onLoad={() => {
                      setLoading(false);
                    }}
                    draggable={false}
                  />
                </>
              )}

              {!isPDF && !isImage && (
                <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                  <div>Preview not available for this file type</div>
                  <div style={{ marginTop: 8, fontSize: "12px" }}>
                    File type: {fileType?.toUpperCase() || "Unknown"}
                  </div>
                </div>
              )}

              {/* Watermark Overlay */}
              <WatermarkOverlay />

              {/* Transparent overlay to prevent copying */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "transparent",
                  zIndex: 999,
                }}
                onContextMenu={handleContextMenu}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
